import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  completeTypingDrill,
  fetchTypingLayout,
  fetchTypingLesson,
  fetchTypingLessons,
  fetchTypingVocabDrill,
  getToken,
} from "../../api";
import TypingDrill from "./TypingDrill";
import TypingLessonList from "./TypingLessonList";
import TypingResults from "./TypingResults";
import TypingUpsell from "./TypingUpsell";
import { buildCharIndex } from "./arabicText";
import {
  buildAdaptiveTarget,
  overlayLessonProgress,
  overlayLessons,
  recordDrill,
  recordKeyStats,
} from "./localProgress";

const REMAP_STORAGE_KEY = "typing.remap";

/**
 * TypingPanel — the typing tutor's working surface, shared between the in-app
 * overlay (TypingView) and the public /typing page (PublicTypingPage), the
 * same split as SarfPanel/SarfTool/PublicSarfPage.
 *
 * Signed-in callers persist progress server-side. Anonymous visitors get the
 * same curriculum with progress in localStorage (localProgress.js) — the
 * unlock chain and the adaptive drill are computed client-side, and the Word
 * Bank drill (which needs an account) is hidden.
 */
export default function TypingPanel({ onClose, context = "overlay" }) {
  const anonymous = !getToken();

  const [layout, setLayout] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [vocab, setVocab] = useState({ words: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [view, setView] = useState("list"); // list | drill | results
  const [lesson, setLesson] = useState(null);
  const [drillIndex, setDrillIndex] = useState(0);
  const [result, setResult] = useState(null);
  const [isRecord, setIsRecord] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Remapping is on by default: almost nobody has an Arabic layout installed,
  // and the ones who do can turn it off here.
  const [remap, setRemap] = useState(() => {
    try {
      return localStorage.getItem(REMAP_STORAGE_KEY) !== "false";
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(REMAP_STORAGE_KEY, String(remap));
    } catch {
      /* private browsing — the preference just won't persist */
    }
  }, [remap]);

  const bestBefore = useRef(0);

  // Touch-typing needs a physical keyboard, and screen width is a poor proxy
  // for having one — a phone held in landscape is wider than the `sm` breakpoint
  // and still has no keys. `pointer: fine` is the honest test: a primary
  // pointing device that isn't a fingertip.
  const [canType, setCanType] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return true;
    return window.matchMedia("(min-width: 640px) and (pointer: fine)").matches;
  });
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const query = window.matchMedia("(min-width: 640px) and (pointer: fine)");
    const sync = () => setCanType(query.matches);
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchTypingLayout(),
      fetchTypingLessons(),
      // The Word Bank drill needs an account; don't even ask when anonymous.
      anonymous ? Promise.resolve(null) : fetchTypingVocabDrill().catch(() => null),
    ])
      .then(([layoutData, lessonData, vocabData]) => {
        if (cancelled) return;
        setLayout(layoutData);
        const list = lessonData.lessons || [];
        setLessons(anonymous ? overlayLessons(list) : list);
        if (vocabData) setVocab(vocabData);
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [anonymous]);

  // Escape closes the overlay, as everywhere else in the app. The typing hook
  // never swallows Escape, so this stays reachable mid-drill. The public page
  // passes no onClose — there is nothing to close.
  useEffect(() => {
    if (!onClose) return undefined;
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const charIndex = useMemo(() => buildCharIndex(layout?.keys), [layout]);

  const refreshLessons = useCallback(() => {
    fetchTypingLessons()
      .then((d) => {
        const list = d.lessons || [];
        setLessons(anonymous ? overlayLessons(list) : list);
      })
      .catch(() => {});
  }, [anonymous]);

  const openLesson = useCallback(
    async (code) => {
      setSaveError(null);
      try {
        const data = await fetchTypingLesson(code);
        let progress = data.progress;
        let drills = data.lesson.drills;
        if (anonymous) {
          progress = overlayLessonProgress(data.lesson);
          // The server hands anonymous callers the fixed fallback sentence;
          // build the real weak-key target from localStorage stats.
          const adaptive = buildAdaptiveTarget(layout);
          if (adaptive) {
            drills = drills.map((d) => (d.kind === "adaptive" ? { ...d, target: adaptive } : d));
          }
        }
        setLesson({ ...data.lesson, drills, progress });
        const done = progress.completed_drills;
        const first = drills.findIndex((d) => !done.includes(d.key));
        setDrillIndex(first === -1 ? 0 : first);
        bestBefore.current = progress.best_wpm;
        setView("drill");
      } catch (e) {
        setError(e.message);
      }
    },
    [anonymous, layout]
  );

  /** Word Bank practice — a synthetic lesson, not part of the curriculum. */
  const openVocab = useCallback(async () => {
    setSaveError(null);
    try {
      const data = await fetchTypingVocabDrill();
      setVocab(data);
      setLesson({
        code: null,
        title: "Your Word Bank",
        title_arabic: "بَنْك الْكَلِمَات",
        drills: [
          {
            key: "vocab",
            title: "Your own words",
            intro: data.count
              ? "Vowelled words you have saved. Results here are not recorded against the curriculum."
              : "",
            target: data.words.map((w) => w.text).join(" "),
            kind: "words",
            prefer_ligature: false,
          },
        ],
      });
      setDrillIndex(0);
      bestBefore.current = 0;
      setView("drill");
    } catch (e) {
      setError(e.message);
    }
  }, []);

  const handleDrillComplete = useCallback(
    async (stats) => {
      setResult(stats);
      setView("results");

      if (!lesson?.code) {
        setIsRecord(false);
        return; // Word Bank practice is not persisted.
      }

      if (anonymous) {
        recordKeyStats(stats.keyAttempts, stats.keyErrors);
        const { progress, error: localError } = recordDrill(
          lesson.code,
          lesson.drills.map((d) => d.key),
          lesson.drills[drillIndex].key,
          stats.wpm,
          stats.accuracy
        );
        if (localError) {
          setSaveError(localError);
          setIsRecord(false);
          return;
        }
        setIsRecord(progress.best_wpm > bestBefore.current);
        bestBefore.current = progress.best_wpm;
        setLesson((l) => (l ? { ...l, progress } : l));
        refreshLessons();
        return;
      }

      try {
        const data = await completeTypingDrill(lesson.code, {
          drill: lesson.drills[drillIndex].key,
          wpm: stats.wpm,
          accuracy: stats.accuracy,
          keyAttempts: stats.keyAttempts,
          keyErrors: stats.keyErrors,
        });
        setIsRecord(data.progress.best_wpm > bestBefore.current);
        bestBefore.current = data.progress.best_wpm;
        setLesson((l) => (l ? { ...l, progress: data.progress } : l));
        refreshLessons();
      } catch (e) {
        setSaveError(e.message);
        setIsRecord(false);
      }
    },
    [anonymous, lesson, drillIndex, refreshLessons]
  );

  const backToList = useCallback(() => {
    setView("list");
    setLesson(null);
    setResult(null);
    setSaveError(null);
    refreshLessons();
    if (!anonymous) fetchTypingVocabDrill().then(setVocab).catch(() => {});
  }, [anonymous, refreshLessons]);

  const isLastDrill = lesson && drillIndex >= lesson.drills.length - 1;

  const handleNext = useCallback(() => {
    if (isLastDrill) {
      backToList();
      return;
    }
    setDrillIndex((i) => i + 1);
    setResult(null);
    setView("drill");
  }, [isLastDrill, backToList]);

  const handleRetry = useCallback(() => {
    setResult(null);
    setView("drill");
  }, []);

  const drill = lesson?.drills?.[drillIndex];
  const emptyVocab = lesson && !lesson.code && !drill?.target;

  return (
    <div
      className={`surface-glass dark-scope rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col ${
        context === "public" ? "h-[85vh] mt-2" : "h-[92vh]"
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* ── header ── */}
      <div className="flex items-center justify-between px-6 py-4 shrink-0" style={{ borderBottom: "1px solid var(--panel-line)" }}>
        <div className="flex items-center gap-3 min-w-0">
          {view !== "list" && (
            <button
              onClick={backToList}
              className="p-1.5 rounded-lg transition-[filter] hover:brightness-125"
              style={{ color: "var(--parchment-dim)" }}
              aria-label="Back to lessons"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          <div className="min-w-0">
            <h2 className="font-cormorant font-semibold text-lg truncate" style={{ color: "var(--gold-bright)" }}>
              {view === "list" ? "Typing" : lesson?.title}
            </h2>
            <p className="text-xs" style={{ color: "var(--parchment-dim)" }}>
              {view === "list"
                ? "Learn to type Arabic — letters, then the ḥarakāt."
                : `Drill ${drillIndex + 1} of ${lesson?.drills?.length || 1}`}
            </p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-[filter] hover:brightness-125"
            style={{ color: "var(--parchment-dim)" }}
            aria-label="Close"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* ── no physical keyboard: say so plainly rather than half-working ── */}
      {!canType && (
      <div className="flex-1 flex items-center justify-center px-8 text-center">
        <div>
          <div className="text-4xl mb-3" aria-hidden="true">
            ⌨️
          </div>
          <p className="font-cormorant font-semibold text-lg" style={{ color: "var(--gold-bright)" }}>
            Open this on a computer
          </p>
          <p className="text-sm mt-2" style={{ color: "var(--parchment-dim)" }}>
            {context === "public"
              ? "Learning to type Arabic means learning where the keys are, so this tool needs a physical keyboard. Come back on a laptop or desktop — it takes two minutes to start."
              : "Learning to type Arabic means learning where the keys are, so this section needs a physical keyboard. Everything else in Murājaʿa works here on your phone."}
          </p>
        </div>
      </div>
      )}

      {/* ── the tutor ── */}
      {canType && (
      <div className="flex flex-1 flex-col overflow-y-auto scroll-elegant px-6 py-5">
        {loading && (
          <p className="text-sm m-auto" style={{ color: "var(--parchment-dim)" }}>
            Loading…
          </p>
        )}

        {error && !loading && (
          <p className="text-sm m-auto" style={{ color: "#e58c8c" }}>
            {error}
          </p>
        )}

        {!loading && !error && view === "list" && (
          <>
            {anonymous && <TypingUpsell kind="list" context={`typing-${context}`} />}
            <TypingLessonList
              lessons={lessons}
              layout={layout}
              onSelect={openLesson}
              onVocab={anonymous ? undefined : openVocab}
              vocabCount={vocab.count}
            />
          </>
        )}

        {!loading && !error && view === "drill" && drill && !emptyVocab && (
          <TypingDrill
            drill={drill}
            layout={layout}
            charIndex={charIndex}
            remap={remap}
            onComplete={handleDrillComplete}
            onExit={backToList}
          />
        )}

        {!loading && !error && view === "drill" && emptyVocab && (
          <div className="m-auto text-center max-w-sm">
            <p className="font-cormorant font-semibold text-lg" style={{ color: "var(--gold-bright)" }}>
              No vowelled words yet
            </p>
            <p className="text-sm mt-2" style={{ color: "var(--parchment-dim)" }}>
              Words in your Word Bank show up here once they carry ḥarakāt —
              those are the ones worth practising, since typing the marks is
              the hard part.
            </p>
            <button
              type="button"
              onClick={backToList}
              className="dt-ghost-btn mt-4 px-4 py-2 rounded-xl text-sm font-semibold"
            >
              Back to lessons
            </button>
          </div>
        )}

        {!loading && !error && view === "results" && result && (
          <div className="max-w-lg w-full mx-auto my-auto">
            {saveError && (
              <p className="text-sm mb-3 text-center" style={{ color: "#e58c8c" }}>
                {saveError}
              </p>
            )}
            <TypingResults
              result={result}
              layout={layout}
              isRecord={isRecord}
              onRetry={handleRetry}
              onNext={handleNext}
              nextLabel={isLastDrill ? "Back to lessons" : "Next drill"}
            />
            {anonymous && <TypingUpsell kind="results" context={`typing-${context}`} />}
          </div>
        )}
      </div>
      )}

      {/* ── footer: input mode ── */}
      {canType && (
      <div
        className="flex items-center justify-between px-6 py-3 shrink-0 gap-4"
        style={{ borderTop: "1px solid var(--panel-line)" }}
      >
        <p className="text-xs" style={{ color: "var(--parchment-dim)" }}>
          {remap
            ? "Your keyboard is being mapped to the Arabic 101 layout — no setup needed."
            : "Using your system’s Arabic keyboard layout."}
        </p>
        <label className="flex items-center gap-2 text-xs cursor-pointer shrink-0" style={{ color: "var(--parchment-dim)" }}>
          <input
            type="checkbox"
            checked={!remap}
            onChange={(e) => setRemap(!e.target.checked)}
            className="accent-current"
          />
          I have Arabic installed
        </label>
      </div>
      )}
    </div>
  );
}
